/**
 * Shared percent formatters for the experiments feature charts.
 *
 * Two unit-explicit entry points so the contract is clear at the call site:
 *
 *   - `formatPercentFromDecimal(0.042)` → `"+4.2%"`
 *   - `formatPercentFromPercent(4.2)`   → `"+4.2%"`
 *
 * Each accepts an optional `{ precision }` to override the default rule.
 */

interface PrecisionOptions {
  /**
   * `"chart"` (default) — 1 dp when `|pct| < 10`, 0 dp otherwise. Matches the
   * cumulative-trend axis convention.
   * `"chart-1dp"` — always 1 dp; matches the daily-lift / weekly-DiD chart
   * conventions where small-magnitude trend changes need consistent
   * precision and the axis range stays inside ±50%.
   * `"table-2dp"` — 2 dp when `|pct| < 10`, 1 dp otherwise; for table cells
   * that need higher fidelity than chart axes.
   */
  precision?: 'chart' | 'chart-1dp' | 'table-2dp';
}

// `<=` (not strict `<`) keeps the boundary value ±0.05 itself inside the
// collapse window — without this, `(0.05).toFixed(1)` was `"0.1"`, so the
// axis tick exactly at the threshold rendered `"+0.1%"` adjacent to `"0%"`.
const ZERO_THRESHOLD = 0.05;

function precisionAt(pct: number, mode: PrecisionOptions['precision']): number {
  switch (mode) {
    case 'chart-1dp':
      return 1;
    case 'table-2dp':
      return Math.abs(pct) < 10 ? 2 : 1;
    case 'chart':
    default:
      return Math.abs(pct) < 10 ? 1 : 0;
  }
}

// Per-mode rendering for the collapsed-zero case. Chart mode (default)
// emits a unitless "0%" baseline that visually rests on the y=0 gridline;
// always-1-dp axes match neighbouring tick widths with "0.0%"; the
// 2-dp table convention matches cell widths with "0.00%".
const ZERO_FORMAT = {
  chart: '0%',
  'chart-1dp': '0.0%',
  'table-2dp': '0.00%',
} as const;

function formatPctValue(pct: number, opts: PrecisionOptions = {}): string {
  if (!Number.isFinite(pct)) return '—';
  const mode = opts.precision ?? 'chart';
  if (Math.abs(pct) <= ZERO_THRESHOLD) return ZERO_FORMAT[mode];
  const dp = precisionAt(pct, mode);
  const rounded = pct.toFixed(dp);
  const sign = pct > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

/** Format a decimal lift (0.042 → "+4.2%"). Default chart-axis precision. */
export function formatPercentFromDecimal(value: number, opts?: PrecisionOptions): string {
  return formatPctValue(value * 100, opts);
}

/** Format an already-percent value (4.2 → "+4.2%"). Default chart-axis precision. */
export function formatPercentFromPercent(value: number, opts?: PrecisionOptions): string {
  return formatPctValue(value, opts);
}
